import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import SelectItemWithDescription, {
  ItemWithDescription,
} from "../../../SelectItemWithDescription/SelectItemWithDescription";
import { useTranslation } from "@coworker/locales";

export type SelectItemWithDescriptionCreatorFragmentConfigProps = {
  // Translation key for the title
  title: string;
  field: string;
  items: ItemWithDescription[];
};

type SelectItemWithDescriptionCreatorFragmentProps = {
  configProps: SelectItemWithDescriptionCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

type Item = {
  value: string;
  // Translation key for the label
  label: string;
  description: string;
};

function SelectItemWithDescriptionCreatorFragment({
  configProps,
  commonProps,
  ref,
}: SelectItemWithDescriptionCreatorFragmentProps) {
  const { field, title, items } = configProps;
  const { blockInput, task, onTaskChange, showValidation } = commonProps;
  const item = getByPath<Item>(task, field);
  const { t } = useTranslation();

  const handleConfirmClick = useCallback(
    (newItem: Item) => {
      onTaskChange(
        setByPath(field, {
          value: newItem.value,
          label: newItem.label,
          description: newItem.description,
        })
      );
    },
    [field, onTaskChange]
  );

  const valid = !isLoading(item) && item !== null;

  useImperativeHandle(ref, () => {
    return { valid };
  });

  if (isLoading(item)) {
    return <LoadingCreatorFragment />;
  }

  return (
    <SelectItemWithDescription
      title={t(title)}
      label={item?.label ?? ""}
      description={item?.description ?? ""}
      value={item?.value ?? ""}
      items={items}
      onConfirmClick={handleConfirmClick}
      disabled={blockInput}
      isError={!valid && showValidation}
    />
  );
}

export default SelectItemWithDescriptionCreatorFragment;
